import {useId} from 'react'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {CounterLabel} from '@primer/react'
import {Disclosure} from '@github-ui/disclosure'
import type {RegisteredRuleSchemaComponent} from '@github-ui/repos-rules-types/rules-types'
import {FilePathList} from './FilePathList'

import styles from './AllowedExceptions.module.css'

export function AllowedExceptions({
  field,
  value,
  onValueChange,
  readOnly,
  ruleParameters,
}: RegisteredRuleSchemaComponent) {
  const exceptionsId = useId()
  const description = field.description
  const ignoredFilePaths = (value as string[] | undefined) ?? []
  const restrictedFilePaths = ruleParameters?.['restricted_file_paths'] as string[] | undefined
  // This disables the "Add" button and also hides existing ignored paths. Used when there are no restricted paths,
  // so that the user won't think that ignored paths do anything (they won't).
  const disableAll =
    field.ui_control === 'path_restriction_ignored_file_paths' && (restrictedFilePaths?.length ?? 0) === 0

  if (readOnly && ignoredFilePaths.length === 0) return null

  return (
    <Disclosure defaultOpen={!disableAll && ignoredFilePaths.length > 0}>
      <Disclosure.Trigger className="mt-3">
        <div className={styles.triggerContents}>
          <span>Allowed exceptions</span>
          <CounterLabel>{ignoredFilePaths.length}</CounterLabel>
          <Disclosure.Chevron />
          <BetaLabel />
        </div>
      </Disclosure.Trigger>

      <Disclosure.Panel>
        {description && <span className={styles.descriptionText}>{description}</span>}
        <div id={exceptionsId} className="mt-3">
          <FilePathList
            field={field}
            value={value}
            onValueChange={onValueChange}
            readOnly={readOnly}
            disableAll={disableAll}
            boxName="Allowed exceptions"
            buttonName="Add allowed path"
            blankslate={disableAll ? 'Add a restricted path before adding exceptions' : 'No allowed exceptions'}
          />
        </div>
      </Disclosure.Panel>
    </Disclosure>
  )
}
